import React, { useMemo, useState } from "react";
import {
  Plus,
  Loader2,
  Send,
  CheckCircle2,
  PackageCheck,
  RotateCcw,
} from "lucide-react";
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import ProductLineCard from "./ProductLineCard";
import priceRequestsApi from "../../services/priceRequestsApi";

const MAX_LINES = 100;

const makeEmptyLine = () => ({
  description: "",
  brand: "",
  model: "",
  manufacturer: "",
  quantity: 1,
  category_id: "",
  image: null, // { file, previewUrl } | null
});

const ProductRequestForm = ({ user, categories, onSubmitted }) => {
  const [lines, setLines] = useState([makeEmptyLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(""); // aria-live
  const [success, setSuccess] = useState(null); // { requestNumber }

  const canAddMore = lines.length < MAX_LINES;
  const totalItems = useMemo(
    () => lines.reduce((n, l) => n + (Number(l.quantity) || 0), 0),
    [lines]
  );

  const updateLine = (idx, next) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? next : l)));

  const addLine = () => {
    if (!canAddMore) return;
    setLines((prev) => [...prev, makeEmptyLine()]);
  };

  const removeLine = (idx) =>
    setLines((prev) => prev.filter((_, i) => i !== idx));

  const validate = () => {
    if (!user?.username) {
      return "You need to be signed in to submit a quotation request.";
    }
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.description || !l.description.trim()) {
        return `Product #${i + 1}: description is required.`;
      }
      if (l.description.length > 1000) {
        return `Product #${i + 1}: description must be 1000 characters or fewer.`;
      }
      if (!l.quantity || Number(l.quantity) < 1) {
        return `Product #${i + 1}: quantity must be at least 1.`;
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("");
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    try {
      setSubmitting(true);
      setStatus("Submitting your quotation request...");

      // Blexbot expects a single `lines` field with a JSON array plus
      // separate `product_images` file parts, referenced by `image_index`.
      // See the upstream builder in /price_requests/us(...) — reverse
      // engineered from app.blexbot.com's public bundle.
      const files = [];
      const linesPayload = lines.map((l) => {
        const line = {
          description: (l.description || "").trim(),
          brand: (l.brand || "").trim(),
          model: (l.model || "").trim(),
          manufacturer: (l.manufacturer || "").trim(),
          quantity: Math.max(1, parseInt(l.quantity, 10) || 1),
        };
        if (l.image?.file) {
          line.image_index = files.length;
          files.push(l.image.file);
        }
        return line;
      });

      const fd = new FormData();
      fd.append("lines", JSON.stringify(linesPayload));
      files.forEach((file) => fd.append("product_images", file, file.name));

      const data = await priceRequestsApi.createRequest(fd);
      const requestNumber =
        data?.request_number ||
        data?.number ||
        data?.code ||
        data?.data?.request_number ||
        data?.data?.number ||
        data?.request?.number ||
        data?.request?.request_number ||
        "";
      setSuccess({ requestNumber, raw: data });
      setStatus(
        requestNumber
          ? `Request ${requestNumber} was submitted successfully.`
          : "Your request was submitted successfully."
      );
      onSubmitted && onSubmitted({ requestNumber, raw: data });
    } catch (err) {
      setError(err.message || "We could not submit the request. Please try again.");
      setStatus("");
    } finally {
      setSubmitting(false);
    }
  };

  const startNew = () => {
    lines.forEach((l) => l.image?.previewUrl && URL.revokeObjectURL(l.image.previewUrl));
    setLines([makeEmptyLine()]);
    setSuccess(null);
    setError("");
    setStatus("");
  };

  if (success) {
    return (
      <div data-testid="product-request-success" className="text-center py-6 space-y-4">
        <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-green-700" aria-hidden="true" />
        </div>
        <h3 className="text-2xl font-bold text-[#1E3A5F]">
          Request submitted!
        </h3>
        <p className="text-slate-600 max-w-md mx-auto">
          Our sourcing team is on it. You'll see it appear in <em>My previous requests</em> below
          and receive an update when a quote is ready.
        </p>
        {success.requestNumber && (
          <p
            className="inline-block text-sm font-semibold rounded-full border border-cyan-300 bg-cyan-50 text-cyan-800 px-4 py-2"
            data-testid="pr-request-number"
          >
            Request #{success.requestNumber}
          </p>
        )}
        <div className="flex justify-center">
          <Button
            type="button"
            onClick={startNew}
            data-testid="pr-start-new-btn"
            className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full px-6"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Start a new request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="product-request-form"
      className="space-y-5"
    >
      <div className="flex items-start gap-3">
        <div className="bg-cyan-100 rounded-full p-3">
          <PackageCheck className="w-6 h-6 text-cyan-700" aria-hidden="true" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#1E3A5F]">Add your products</h3>
          <p className="text-sm text-slate-600">
            Add up to {MAX_LINES} products in a single request. For accurate quotes, include
            brand, model and a clear photo when available.
          </p>
        </div>
      </div>

      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertDescription className="text-red-800" data-testid="pr-form-error">
            {error}
          </AlertDescription>
        </Alert>
      )}
      <p className="sr-only" aria-live="polite" data-testid="pr-form-status">
        {status}
      </p>

      <div className="space-y-4">
        {lines.map((line, idx) => (
          <ProductLineCard
            key={idx}
            index={idx}
            value={line}
            onChange={(next) => updateLine(idx, next)}
            onRemove={() => removeLine(idx)}
            canRemove={lines.length > 1}
            categories={categories}
          />
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2 border-t border-slate-200">
        <div className="text-sm text-slate-600">
          <span className="font-semibold">{lines.length}</span> product line
          {lines.length === 1 ? "" : "s"} · <span className="font-semibold">{totalItems}</span>{" "}
          total item{totalItems === 1 ? "" : "s"}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={addLine}
            disabled={!canAddMore}
            data-testid="pr-add-line-btn"
            className="rounded-full border-cyan-400 text-cyan-700 hover:bg-cyan-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add another product
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="pr-submit-request-btn"
            className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-full px-6"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit quotation request
              </>
            )}
          </Button>
        </div>
      </div>

      {!canAddMore && (
        <p className="text-xs text-slate-500 text-right">
          You've reached the {MAX_LINES}-product limit.
        </p>
      )}
    </form>
  );
};

export default ProductRequestForm;
